/**
 * PAPPO — ProfilePage
 * Aba "Perfil": o utilizador vê e edita a sua foto e o nome de exibição.
 * O @username é imutável (só leitura). Espelha o layout do PermissionsPage /
 * ParentPanelPage (cabeçalho com ícone lucide, secções e cards).
 *
 * Nota: o display_name no JWT só se renova ao voltar a entrar (re-login); o
 * /api/users/me devolve sempre os dados frescos, e chamamos refreshUser() para
 * propagar a mudança à barra (Navbar) sem recarregar a página.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { CircleUser, Camera, Check, AtSign, User as UserIcon } from 'lucide-react';
import { Avatar } from '../components/common/Avatar';
import { ImageCropper } from '../components/common/ImageCropper';
import { uploadImage } from '../services/uploads';
import { updateProfile } from '../services/profile';
import { listBlocks, unblockUser, type BlockedUser } from '../services/blocks';
import { useAuth } from '../hooks/useAuth';
import styles from './ProfilePage.module.css';

type Feedback = { type: 'success' | 'error'; msg: string } | null;

export function ProfilePage() {
  const { user, refreshUser } = useAuth();

  const [displayName, setDisplayName] = useState(user?.display_name ?? '');
  const [avatarUrl, setAvatarUrl] = useState<string | null>(user?.avatar_url ?? null);
  const [fileToCrop, setFileToCrop] = useState<File | null>(null);
  const [savingName, setSavingName] = useState(false);
  const [savingPhoto, setSavingPhoto] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const trimmed = displayName.trim();
  const nameChanged = !!user && trimmed !== '' && trimmed !== user.display_name;

  /* Recorte concluído → upload → PUT /users/me com a nova avatar_url */
  const handleCropApply = useCallback(
    async (cropped: File) => {
      setFileToCrop(null);
      setSavingPhoto(true);
      setFeedback(null);
      const up = await uploadImage(cropped);
      if (!up.success || !up.data) {
        setSavingPhoto(false);
        setFeedback({ type: 'error', msg: 'Não foi possível enviar a foto.' });
        return;
      }
      const res = await updateProfile({ avatar_url: up.data.attachment_url });
      setSavingPhoto(false);
      if (res.success) {
        // Usa a avatar_url ASSINADA (?exp=&sig=) devolvida pelo PUT /me — a URL
        // CRUA do upload dá 403 na rota /uploads e a foto quebra. Fallback à
        // crua só se, por algum motivo, o backend não devolver o campo.
        setAvatarUrl(res.data?.avatar_url ?? up.data.attachment_url);
        setFeedback({ type: 'success', msg: 'Foto atualizada.' });
        refreshUser(); // propaga à Navbar sem recarregar (busca /me assinado)
      } else {
        setFeedback({ type: 'error', msg: res.error || 'Falha ao guardar a foto.' });
      }
    },
    [refreshUser],
  );

  /* Guardar o novo nome de exibição */
  const handleSaveName = useCallback(async () => {
    if (!nameChanged || savingName) return;
    setSavingName(true);
    setFeedback(null);
    const res = await updateProfile({ display_name: trimmed });
    setSavingName(false);
    if (res.success) {
      setFeedback({ type: 'success', msg: 'Nome atualizado.' });
      refreshUser();
    } else {
      setFeedback({ type: 'error', msg: res.error || 'Falha ao guardar o nome.' });
    }
  }, [nameChanged, savingName, trimmed, refreshUser]);

  /* Bloqueados: carrega a lista e permite desbloquear */
  const [blocked, setBlocked] = useState<BlockedUser[]>([]);
  const loadBlocked = useCallback(async () => {
    const res = await listBlocks();
    if (res.success && res.data) setBlocked(res.data.blocked);
  }, []);
  useEffect(() => {
    loadBlocked();
  }, [loadBlocked]);
  const handleUnblock = useCallback(async (id: number) => {
    const res = await unblockUser(id);
    if (res.success) setBlocked((b) => b.filter((u) => u.id !== id));
  }, []);

  if (!user) return null;

  return (
    <div className={styles.page}>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <div className={styles.titleRow}>
          <CircleUser size={28} className={styles.titleIcon} />
          <div>
            <h1 className={styles.title}>Perfil</h1>
            <p className={styles.subtitle}>A sua foto e o seu nome de exibição</p>
          </div>
        </div>
      </div>

      {feedback && (
        <div
          className={`${styles.feedback} ${
            feedback.type === 'success' ? styles.ok : styles.err
          }`}
          role="status"
        >
          {feedback.msg}
        </div>
      )}

      {/* Foto */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Foto</h2>
        <div className={styles.card}>
          <div className={styles.avatarWrap}>
            <Avatar name={displayName || user.display_name} src={avatarUrl} size="lg" />
          </div>
          <div className={styles.cardInfo}>
            <span className={styles.cardHint}>
              Escolha uma imagem e enquadre-a num quadrado. Fica melhor um rosto
              centrado.
            </span>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() => fileInputRef.current?.click()}
              disabled={savingPhoto}
            >
              <Camera size={16} />
              {savingPhoto ? 'A guardar...' : 'Trocar foto'}
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) setFileToCrop(f);
              e.target.value = '';
            }}
          />
        </div>
      </section>

      {/* Dados */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Dados</h2>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="display_name">
            <UserIcon size={15} /> Nome de exibição
          </label>
          <div className={styles.inputRow}>
            <input
              id="display_name"
              className={styles.input}
              value={displayName}
              maxLength={80}
              onChange={(e) => setDisplayName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveName();
              }}
            />
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={handleSaveName}
              disabled={!nameChanged || savingName}
            >
              <Check size={16} />
              {savingName ? 'A guardar...' : 'Guardar'}
            </button>
          </div>
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="username">
            <AtSign size={15} /> Nome de utilizador
          </label>
          <input
            id="username"
            className={`${styles.input} ${styles.readonly}`}
            value={`@${user.username}`}
            readOnly
            disabled
          />
          <span className={styles.cardHint}>
            O nome de utilizador é fixo e não pode ser alterado.
          </span>
        </div>
      </section>

      {/* Bloqueados */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Bloqueados</h2>
        <div className={styles.card}>
          {blocked.length === 0 ? (
            <span className={styles.cardHint}>Você não bloqueou ninguém.</span>
          ) : (
            <ul
              style={{
                listStyle: 'none',
                margin: 0,
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                width: '100%',
              }}
            >
              {blocked.map((u) => (
                <li key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Avatar name={u.display_name} src={u.avatar_url} size="sm" />
                  <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                    <span style={{ fontWeight: 500 }}>{u.display_name}</span>
                    <span className={styles.cardHint}>@{u.username}</span>
                  </div>
                  <button
                    type="button"
                    className={styles.secondaryBtn}
                    onClick={() => handleUnblock(u.id)}
                  >
                    Desbloquear
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {fileToCrop && (
        <ImageCropper
          file={fileToCrop}
          onCancel={() => setFileToCrop(null)}
          onApply={handleCropApply}
        />
      )}
    </div>
  );
}
